/**
 * Internal Hook: useDeleteMeetingMutation
 *
 * Encapsulates the TanStack Query mutation logic for deleting a scheduled meeting.
 * It provides a responsive user experience by leveraging optimistic updates:
 * - `onMutate`: Immediately removes the meeting from the cache for instant UI feedback.
 * - `onError`: Rolls back the cache to its previous state on failure.
 * - `onSuccess`: Invalidates meeting lists and cleans up the detail cache for the deleted meeting
 *   to ensure final data consistency.
 */
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { SCHEDULED_MEETINGS_QUERY_KEYS } from '../helpers';
import type { ScheduledMeeting, ScheduledMeetingsProviderOptions } from '../types';
import useScheduledMeetingsApi from './useScheduledMeetingsApi';

const useDeleteMeetingMutation = (options: ScheduledMeetingsProviderOptions) => {
  const queryClient = useQueryClient();
  const api = useScheduledMeetingsApi();

  const deleteMutation = useMutation({
    mutationFn: async (room: string) => {
      await api.deleteMeeting(room);
      return room;
    },
    onMutate: async (room) => {
      // Cancel outgoing refetches
      const queryKey = SCHEDULED_MEETINGS_QUERY_KEYS.list(options.initialQuery);
      await queryClient.cancelQueries({ queryKey });

      const previousMeetings = queryClient.getQueryData<InfiniteData<ScheduledMeeting[]>>(queryKey);

      // Optimistically update
      if (previousMeetings) {
        queryClient.setQueryData<InfiniteData<ScheduledMeeting[]>>(queryKey, (oldData) => {
          if (!oldData) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => page.filter((meeting) => meeting.room !== room)),
          };
        });
      }

      return { previousMeetings };
    },
    onError: (_error, _room, context) => {
      // Rollback on error
      if (context?.previousMeetings) {
        queryClient.setQueryData(
          SCHEDULED_MEETINGS_QUERY_KEYS.list(options.initialQuery),
          context.previousMeetings,
        );
      }
    },
    onSuccess: (room) => {
      // Invalidate all lists
      queryClient.invalidateQueries({
        queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.lists(),
      });

      // Remove detail cache for deleted meeting
      queryClient.removeQueries({
        queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.detail(room),
      });
    },
  });

  const mutateAsync = useCallback(
    async (room: string): Promise<void> => {
      await deleteMutation.mutateAsync(room);
    },
    [deleteMutation],
  );

  return {
    ...deleteMutation,
    mutateAsync,
  };
};

export default useDeleteMeetingMutation;
